import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Crown, Users, Package, TrendingUp, 
  Plus, Trash2, Image, Sparkles, RefreshCw, Upload, 
  FolderOpen, Settings, Layers, Save, X, Check, Edit2,
  Video, FileImage, Grid, List, Search, Filter, Newspaper, Calendar,
  ShoppingBag, FileText, Mail, Euro, UserCheck, DollarSign, Eye, Clock, Coins, Send
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import Navbar from '../components/Navbar';
import ContentManager from '../components/ContentManager';
import MediaManager from '../components/MediaManager';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  
  // State
  const [stats, setStats] = useState(null);
  const [content, setContent] = useState([]);
  const [media, setMedia] = useState([]);
  const [bundles, setBundles] = useState({});
  const [transactions, setTransactions] = useState([]);
  const [siteSettings, setSiteSettings] = useState({});
  const [siteImages, setSiteImages] = useState({});
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [savingImages, setSavingImages] = useState(false);
  const [savingNews, setSavingNews] = useState(false);
  const [shopProducts, setShopProducts] = useState([]);
  const [savingShopProducts, setSavingShopProducts] = useState(false);
  const [shopProductDialog, setShopProductDialog] = useState(false);
  const [editingShopProduct, setEditingShopProduct] = useState(null);
  const [shopProductForm, setShopProductForm] = useState({
    id: '',
    name: '',
    description: '',
    price: 0,
    image_url: '',
    file_url: '',
    category: 'digital',
    is_active: true,
    gallery_folder: '',
    token_price: 0
  });
  // Packages state
  const [packages, setPackages] = useState([]);
  const [packageDialog, setPackageDialog] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [packageForm, setPackageForm] = useState({
    id: '',
    name: '',
    description: '',
    token_price: 0,
    euro_price: 0,
    image_url: '',
    gallery_folder: '',
    features: [],
    is_active: true,
    type: 'subscription' // subscription, lifetime, bundle
  });
  const [contactEmail, setContactEmail] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);
  const [members, setMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberSearch, setMemberSearch] = useState('');
  const [autoNewsEnabled, setAutoNewsEnabled] = useState(true);
  const [adminTokenBalance, setAdminTokenBalance] = useState(0);
  const [tokenTransferDialog, setTokenTransferDialog] = useState(null);
  const [tokenAmount, setTokenAmount] = useState(10);
  const [tokenReason, setTokenReason] = useState('');
  const [sendingTokens, setSendingTokens] = useState(false);
  
  // Vault configuration
  const [vaultConfig, setVaultConfig] = useState({ gallery_id: '', name: 'Secret Vault', min_tier: 'diamond' });
  const [savingVault, setSavingVault] = useState(false);
  
  // Ella Chat configuration
  const [ellaChatImages, setEllaChatImages] = useState([]);
  const [newEllaChatImage, setNewEllaChatImage] = useState('');
  const [savingEllaConfig, setSavingEllaConfig] = useState(false);
  
  // Video upload
  const videoInputRef = useRef(null);
  
  // Dialogs
  const [contentDialog, setContentDialog] = useState(false);
  const [mediaDialog, setMediaDialog] = useState(false);
  const [bundleDialog, setBundleDialog] = useState(false);
  const [imagePickerDialog, setImagePickerDialog] = useState(false);
  const [newsDialog, setNewsDialog] = useState(false);
  const [currentImageLocation, setCurrentImageLocation] = useState(null);
  const [editingNews, setEditingNews] = useState(null);
  
  // Current selections
  const [selectedPackage, setSelectedPackage] = useState('bronze');
  const [selectedFolder, setSelectedFolder] = useState('gallery');
  const [viewMode, setViewMode] = useState('grid');
  
  // Form data
  const [newContent, setNewContent] = useState({
    title: '',
    description: '',
    content_type: 'photo',
    image_url: '',
    preview_url: '',
    price_tier: 'bronze',
    is_featured: false
  });

  const [newsForm, setNewsForm] = useState({
    title: '',
    title_nl: '',
    content: '',
    content_nl: '',
    image_url: '',
    instagram_url: '',
    category: 'news',
    is_published: true
  });
  
  const [fetchingInstagram, setFetchingInstagram] = useState(false);
  const [instagramInput, setInstagramInput] = useState('');

  // Website image locations config
  const imageLocations = [
    { id: 'hero', name: 'Hero Achtergrond', section: 'Landing Page' },
    { id: 'gallery_1', name: 'Gallery Afbeelding 1', section: 'Landing Page' },
    { id: 'gallery_2', name: 'Gallery Afbeelding 2', section: 'Landing Page' },
    { id: 'gallery_3', name: 'Gallery Afbeelding 3', section: 'Landing Page' },
    { id: 'gallery_4', name: 'Gallery Afbeelding 4', section: 'Landing Page' },
    { id: 'featured', name: 'Featured Afbeelding', section: 'Landing Page' },
    { id: 'gallery_item_1', name: 'Gallery Item 1', section: 'Gallery Page' },
    { id: 'gallery_item_2', name: 'Gallery Item 2', section: 'Gallery Page' },
    { id: 'gallery_item_3', name: 'Gallery Item 3', section: 'Gallery Page' },
    { id: 'gallery_item_4', name: 'Gallery Item 4', section: 'Gallery Page' },
    { id: 'gallery_item_5', name: 'Gallery Item 5', section: 'Gallery Page' },
    { id: 'gallery_item_6', name: 'Gallery Item 6', section: 'Gallery Page' },
    { id: 'gallery_item_7', name: 'Gallery Item 7', section: 'Gallery Page' },
    { id: 'gallery_item_8', name: 'Gallery Item 8', section: 'Gallery Page' },
    // Shop Product Images
    { id: 'prompt_guide', name: 'Prompt Guide (PDF)', section: 'Shop' },
    { id: 'hd_pack_10', name: '10 HD Art Pack', section: 'Shop' },
    { id: 'hd_pack_50', name: '50 HD Art Pack', section: 'Shop' },
    { id: 'video_pack', name: 'Motion Art Collection', section: 'Shop' },
    { id: 'wallpaper_pack', name: 'Digital Wallpapers', section: 'Shop' },
    { id: '4k_collection', name: '4K Art Collection', section: 'Shop' },
    { id: 'ai_girlfriend', name: 'Custom AI Model', section: 'Shop' },
    { id: 'shop_featured', name: 'Shop Banner', section: 'Shop' },
    { id: 'login_bg', name: 'Login Achtergrond', section: 'Auth' },
    { id: 'register_bg', name: 'Register Achtergrond', section: 'Auth' },
  ];

  // Package tiers config
  const packageTiers = [
    { id: 'bronze', name: 'Bronze', color: '#CD7F32' },
    { id: 'silver', name: 'Silver', color: '#C0C0C0' },
    { id: 'gold', name: 'Gold', color: '#D4AF37' },
    { id: 'platinum', name: 'Platinum', color: '#E5E4E2' },
  ];

  const folders = [
    { id: 'gallery', name: 'Gallery', icon: Grid },
    { id: 'landing', name: 'Landing Page', icon: FileImage },
    { id: 'shop', name: 'Shop', icon: Package },
    { id: 'bundles', name: 'Package Bundles', icon: Layers },
  ];

  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate('/login');
      } else if (!user.is_admin) {
        navigate('/dashboard');
        toast.error('Geen admin toegang');
      } else {
        fetchData();
      }
    }
  }, [user, authLoading, navigate]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [statsRes, contentRes, mediaRes, bundlesRes, txRes, settingsRes, imagesRes, newsRes, shopRes, membersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API}/content`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/media`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/bundles`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/transactions`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/settings`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API}/site-images`).catch(() => ({ data: {} })),
        axios.get(`${API}/admin/news`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/shop-products`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/members/detailed`, { headers }).catch(() => axios.get(`${API}/admin/members`, { headers }).catch(() => ({ data: [] })))
      ]);
      
      setStats(statsRes.data);
      setContent(contentRes.data);
      setMedia(mediaRes.data);
      setTransactions(txRes.data);
      setSiteSettings(settingsRes.data || {});
      setSiteImages(imagesRes.data || {});
      setNews(newsRes.data || []);
      setShopProducts(shopRes.data || []);
      setMembers(membersRes.data || []);
      setContactEmail(settingsRes.data?.social?.email || '');
      setAutoNewsEnabled(settingsRes.data?.news?.auto_news_enabled !== 'false');
      
      // Fetch admin token balance
      try {
        const balanceRes = await axios.get(`${API}/credits/balance`, { headers });
        setAdminTokenBalance(balanceRes.data?.balance || 0);
      } catch (e) {
        console.log('Could not fetch admin balance');
      }
      
      // Fetch Vault config
      try {
        const vaultRes = await axios.get(`${API}/admin/vault/config`, { headers });
        if (vaultRes.data) {
          setVaultConfig(vaultRes.data);
        }
      } catch (e) {
        console.log('Could not fetch vault config');
      }
      
      // Fetch Ella config
      try {
        const ellaRes = await axios.get(`${API}/admin/ella-config`, { headers });
        if (ellaRes.data?.images) {
          setEllaChatImages(ellaRes.data.images);
        }
      } catch (e) {
        console.log('Could not fetch ella config');
      }
      
      // Convert bundles array to object by package_id
      const bundlesObj = {};
      bundlesRes.data.forEach(b => {
        bundlesObj[b.package_id] = b;
      });
      setBundles(bundlesObj);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      toast.error('Kon data niet laden');
    } finally {
      setLoading(false);
    }
  };

  // ============== FILE UPLOAD ==============
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    let successCount = 0;

    for (const file of files) {
      try {
        // Convert to base64
        const base64 = await fileToBase64(file);
        
        // Check if it's a video
        const isVideo = file.type.startsWith('video/');
        const endpoint = isVideo ? `${API}/admin/media/upload-video` : `${API}/admin/media/upload`;
        
        // Check file size for videos (max 50MB)
        if (isVideo && file.size > 50 * 1024 * 1024) {
          toast.error(`Video te groot: ${file.name} (max 50MB)`);
          continue;
        }
        
        const token = localStorage.getItem('token');
        
        // Upload to server
        await axios.post(endpoint, {
          filename: file.name,
          content_type: file.type,
          base64_data: base64.split(',')[1], // Remove data:image/xxx;base64, prefix
          folder: selectedFolder
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        successCount++;
      } catch (error) {
        console.error('Upload failed:', error);
        toast.error(`Upload mislukt: ${file.name}`);
      }
    }

    if (successCount > 0) {
      toast.success(`${successCount} bestand(en) geüpload!`);
      fetchData();
    }
    
    setUploading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  // ============== CONTENT MANAGEMENT ==============
  const handleCreateContent = async () => {
    try {
      await axios.post(`${API}/content`, newContent);
      toast.success('Content toegevoegd!');
      setContentDialog(false);
      setNewContent({
        title: '',
        description: '',
        content_type: 'photo',
        image_url: '',
        preview_url: '',
        price_tier: 'bronze',
        is_featured: false
      });
      fetchData();
    } catch (error) {
      console.error('Failed to create content:', error);
      toast.error('Kon content niet toevoegen');
    }
  };

  const handleDeleteContent = async (id) => {
    if (!confirm('Weet je zeker dat je dit wilt verwijderen?')) return;
    
    try {
      await axios.delete(`${API}/content/${id}`);
      toast.success('Content verwijderd');
      fetchData();
    } catch (error) {
      toast.error('Kon content niet verwijderen');
    }
  };

  const handleDeleteMedia = async (id) => {
    if (!confirm('Weet je zeker dat je dit wilt verwijderen?')) return;
    
    try {
      await axios.delete(`${API}/admin/media/${id}`);
      toast.success('Media verwijderd');
      fetchData();
    } catch (error) {
      toast.error('Kon media niet verwijderen');
    }
  };

  // ============== BUNDLE MANAGEMENT ==============
  const addToBundle = async (contentId, packageId) => {
    try {
      await axios.post(`${API}/admin/bundles/${packageId}/add-content?content_id=${contentId}`);
      toast.success(`Toegevoegd aan ${packageId.toUpperCase()} pakket`);
      fetchData();
    } catch (error) {
      toast.error('Kon niet toevoegen aan pakket');
    }
  };

  const removeFromBundle = async (contentId, packageId) => {
    try {
      await axios.post(`${API}/admin/bundles/${packageId}/remove-content?content_id=${contentId}`);
      toast.success(`Verwijderd uit ${packageId.toUpperCase()} pakket`);
      fetchData();
    } catch (error) {
      toast.error('Kon niet verwijderen uit pakket');
    }
  };

  const isInBundle = (contentId, packageId) => {
    return bundles[packageId]?.content_ids?.includes(contentId) || false;
  };

  // ============== SITE SETTINGS ==============
  const updateSetting = (section, key, value) => {
    setSiteSettings(prev => ({
      ...prev,
      [section]: {
        ...(prev[section] || {}),
        [key]: value
      }
    }));
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      // Convert settings object to array for bulk update
      const settingsArray = [];
      Object.entries(siteSettings).forEach(([section, keys]) => {
        Object.entries(keys).forEach(([key, value]) => {
          settingsArray.push({ section, key, value });
        });
      });
      
      await axios.post(`${API}/admin/settings/bulk`, settingsArray);
      toast.success('Instellingen opgeslagen!');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Kon instellingen niet opslaan');
    } finally {
      setSavingSettings(false);
    }
  };

  // ============== SITE IMAGES ==============
  const updateSiteImage = (location, url) => {
    setSiteImages(prev => ({
      ...prev,
      [location]: {
        ...(prev[location] || {}),
        location,
        image_url: url
      }
    }));
  };

  const saveSiteImages = async () => {
    setSavingImages(true);
    try {
      const imagesArray = Object.values(siteImages).filter(img => img.image_url);
      await axios.post(`${API}/admin/site-images/bulk`, imagesArray);
      toast.success('Afbeeldingen opgeslagen!');
    } catch (error) {
      console.error('Failed to save images:', error);
      toast.error('Kon afbeeldingen niet opslaan');
    } finally {
      setSavingImages(false);
    }
  };

  const openImagePicker = (location) => {
    setCurrentImageLocation(location);
    setImagePickerDialog(true);
  };

  const selectImageForLocation = (imageUrl) => {
    if (currentImageLocation) {
      updateSiteImage(currentImageLocation, imageUrl);
    }
    setImagePickerDialog(false);
  };

  // ============== MEDIA URL HELPER ==============
  const getMediaUrl = (mediaItem) => {
    if (mediaItem.id) {
      return `${API}/media/${mediaItem.id}`;
    }
    return mediaItem.url || mediaItem.image_url;
  };

  // ============== RENDER ==============
  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const filteredMedia = selectedFolder === 'all' 
    ? media 
    : media.filter(m => m.folder === selectedFolder);

  // ============== NEWS FUNCTIONS ==============
  // Default Instagram account
  const INSTAGRAM_ACCOUNT = "@ella.gold.official";
  const INSTAGRAM_PROFILE_URL = "https://www.instagram.com/ella.gold.official";

  const openNewsDialog = (article = null) => {
    if (article) {
      setEditingNews(article);
      setNewsForm({
        title: article.title || '',
        title_nl: article.title_nl || '',
        content: article.content || '',
        content_nl: article.content_nl || '',
        image_url: article.image_url || '',
        instagram_url: article.instagram_url || '',
        category: article.category || 'news',
        is_published: article.is_published !== false
      });
      setInstagramInput(article.instagram_url || '');
    } else {
      setEditingNews(null);
      // Auto-fill with Instagram account info for new posts
      setNewsForm({
        title: '',
        title_nl: '',
        content: `Volg ons op Instagram ${INSTAGRAM_ACCOUNT} voor de laatste updates! 📸✨`,
        content_nl: `Volg ons op Instagram ${INSTAGRAM_ACCOUNT} voor de laatste updates! 📸✨`,
        image_url: '',
        instagram_url: '',
        category: 'instagram',
        is_published: true
      });
      setInstagramInput('');
    }
    setNewsDialog(true);
  };

  const fetchInstagramPost = async () => {
    if (!instagramInput.trim()) {
      toast.error('Voer een Instagram URL in');
      return;
    }

    setFetchingInstagram(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/instagram/fetch`, {
        instagram_url: instagramInput
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setNewsForm({
          ...newsForm,
          title: response.data.title || newsForm.title,
          content: response.data.content || newsForm.content,
          content_nl: response.data.content_nl || newsForm.content_nl,
          image_url: response.data.image_url || newsForm.image_url,
          instagram_url: response.data.instagram_url,
          category: 'instagram'
        });
        toast.success('Instagram post opgehaald! ✨');
      }
    } catch (error) {
      toast.error('Kon Instagram post niet ophalen');
    } finally {
      setFetchingInstagram(false);
    }
  };

  const saveNews = async () => {
    if (!newsForm.title || !newsForm.content) {
      toast.error('Titel en inhoud zijn verplicht');
      return;
    }

    setSavingNews(true);
    try {
      const token = localStorage.getItem('token');
      if (editingNews) {
        await axios.put(`${API}/admin/news/${editingNews.id}`, newsForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Artikel bijgewerkt!');
      } else {
        await axios.post(`${API}/admin/news`, newsForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Artikel aangemaakt!');
      }
      setNewsDialog(false);
      setInstagramInput('');
      fetchData();
    } catch (error) {
      toast.error('Kon artikel niet opslaan');
    } finally {
      setSavingNews(false);
    }
  };

  const deleteNews = async (articleId) => {
    if (!window.confirm('Weet je zeker dat je dit artikel wilt verwijderen?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/news/${articleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Artikel verwijderd!');
      fetchData();
    } catch (error) {
      toast.error('Kon artikel niet verwijderen');
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      
      <div className="pt-24 pb-16 px-6 lg:px-20">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Crown className="w-5 h-5 text-[#D4AF37]" />
              <span className="font-mono text-xs tracking-[0.3em] text-[#D4AF37] uppercase">Admin Panel</span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-medium tracking-tight">
              Content <span className="brand-gold text-4xl sm:text-5xl">Management</span>
            </h1>
          </div>
          <div className="flex gap-3">
            <Button 
              onClick={fetchData}
              variant="outline"
              className="border-white/10 text-white hover:bg-white/5"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Vernieuw
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="p-4 glass rounded-xl">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 text-[#D4AF37]" />
              <div>
                <p className="text-2xl font-bold">{stats?.total_users || 0}</p>
                <p className="text-xs text-gray-400">Gebruikers</p>
              </div>
            </div>
          </div>
          <div className="p-4 glass rounded-xl">
            <div className="flex items-center gap-3">
              <Image className="w-8 h-8 text-[#D4AF37]" />
              <div>
                <p className="text-2xl font-bold">{media.length}</p>
                <p className="text-xs text-gray-400">Media Items</p>
              </div>
            </div>
          </div>
          <div className="p-4 glass rounded-xl">
            <div className="flex items-center gap-3">
              <Package className="w-8 h-8 text-[#D4AF37]" />
              <div>
                <p className="text-2xl font-bold">{stats?.total_purchases || 0}</p>
                <p className="text-xs text-gray-400">Verkopen</p>
              </div>
            </div>
          </div>
          <div className="p-4 glass rounded-xl">
            <div className="flex items-center gap-3">
              <TrendingUp className="w-8 h-8 text-[#D4AF37]" />
              <div>
                <p className="text-2xl font-bold gold-text">€{stats?.total_revenue?.toFixed(2) || '0.00'}</p>
                <p className="text-xs text-gray-400">Omzet</p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Tabs */}
        <Tabs defaultValue="media" className="w-full">
          <TabsList className="bg-white/5 border border-white/10 mb-6 flex-wrap h-auto gap-1 p-1">
            <TabsTrigger value="media" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              <Upload className="w-4 h-4 mr-2" />
              Upload Media
            </TabsTrigger>
            <TabsTrigger value="bundles" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              <Layers className="w-4 h-4 mr-2" />
              Content Manager
            </TabsTrigger>
            <TabsTrigger value="content" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              <Grid className="w-4 h-4 mr-2" />
              All Content
            </TabsTrigger>
            <TabsTrigger value="transactions" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              <TrendingUp className="w-4 h-4 mr-2" />
              Transactions
            </TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              <Settings className="w-4 h-4 mr-2" />
              Site Settings
            </TabsTrigger>
            <TabsTrigger value="images" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              <Image className="w-4 h-4 mr-2" />
              Site Images
            </TabsTrigger>
            <TabsTrigger value="news" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              <Newspaper className="w-4 h-4 mr-2" />
              News
            </TabsTrigger>
            <TabsTrigger value="shop-products" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              <ShoppingBag className="w-4 h-4 mr-2" />
              Shop Products
            </TabsTrigger>
            <TabsTrigger value="email" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              <Mail className="w-4 h-4 mr-2" />
              Contact Email
            </TabsTrigger>
            <TabsTrigger value="members" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              <UserCheck className="w-4 h-4 mr-2" />
              Members
            </TabsTrigger>
            <TabsTrigger value="vault" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              <Crown className="w-4 h-4 mr-2" />
              Vault
            </TabsTrigger>
            <TabsTrigger value="ella-chat" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              <Sparkles className="w-4 h-4 mr-2" />
              Ella Chat
            </TabsTrigger>
            <TabsTrigger value="packages-manage" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              <Package className="w-4 h-4 mr-2" />
              Packages
            </TabsTrigger>
            <TabsTrigger value="analytics" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              <TrendingUp className="w-4 h-4 mr-2" />
              Analytics
            </TabsTrigger>
          </TabsList>

          {/* ============== MEDIA UPLOAD TAB ============== */}
          <TabsContent value="media" className="mt-0">
            <div className="glass rounded-xl p-6">
              <MediaManager />
            </div>
          </TabsContent>

          {/* ============== BUNDLES TAB ============== */}
          <TabsContent value="bundles" className="mt-0">
            <ContentManager />
          </TabsContent>

          {/* ============== ALL CONTENT TAB ============== */}
          <TabsContent value="content" className="mt-0">
            <div className="glass rounded-xl p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-medium">Alle Content ({content.length})</h3>
                <Button onClick={() => setContentDialog(true)} className="btn-gold">
                  <Plus className="w-4 h-4 mr-2" />
                  Nieuwe Content
                </Button>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                {content.map((item) => (
                  <div key={item.id} className="group relative glass rounded-xl overflow-hidden">
                    <img 
                      src={item.preview_url || item.image_url}
                      alt={item.title}
                      className="w-full aspect-[3/4] object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-3">
                      <span className="inline-block px-2 py-0.5 bg-[#D4AF37]/20 text-[#D4AF37] text-[10px] rounded mb-1 uppercase">
                        {item.price_tier}
                      </span>
                      <h4 className="text-sm font-medium truncate">{item.title}</h4>
                    </div>
                    <button
                      onClick={() => handleDeleteContent(item.id)}
                      className="absolute top-2 right-2 p-1.5 bg-red-500/80 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* ============== TRANSACTIONS TAB ============== */}
          <TabsContent value="transactions" className="mt-0">
            <div className="glass rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left p-4 text-sm text-gray-400 font-medium">ID</th>
                      <th className="text-left p-4 text-sm text-gray-400 font-medium">Email</th>
                      <th className="text-left p-4 text-sm text-gray-400 font-medium">Pakket</th>
                      <th className="text-left p-4 text-sm text-gray-400 font-medium">Bedrag</th>
                      <th className="text-left p-4 text-sm text-gray-400 font-medium">Status</th>
                      <th className="text-left p-4 text-sm text-gray-400 font-medium">Datum</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="p-8 text-center text-gray-500">
                          Nog geen transacties
                        </td>
                      </tr>
                    ) : transactions.map((tx) => (
                      <tr key={tx.id} className="border-b border-white/5 hover:bg-white/5">
                        <td className="p-4 text-sm font-mono text-gray-400">{tx.id?.slice(0, 8)}...</td>
                        <td className="p-4 text-sm">{tx.user_email || '-'}</td>
                        <td className="p-4">
                          <span className="px-2 py-1 bg-[#D4AF37]/10 text-[#D4AF37] text-xs rounded uppercase">
                            {tx.package_id}
                          </span>
                        </td>
                        <td className="p-4 text-sm font-medium">€{tx.amount}</td>
                        <td className="p-4">
                          <span className={`px-2 py-1 text-xs rounded ${
                            tx.payment_status === 'paid' 
                              ? 'bg-green-500/20 text-green-400' 
                              : 'bg-yellow-500/20 text-yellow-400'
                          }`}>
                            {tx.payment_status}
                          </span>
                        </td>
                        <td className="p-4 text-sm text-gray-400">
                          {new Date(tx.created_at).toLocaleDateString('nl-NL')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* ============== SITE SETTINGS TAB ============== */}
          <TabsContent value="settings" className="mt-0">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Landing Page Settings */}
              <div className="glass rounded-xl p-6">
                <h3 className="font-medium mb-4 flex items-center gap-2">
                  <Settings className="w-4 h-4 text-[#D4AF37]" />
                  Landing Page Teksten
                </h3>
                <div className="space-y-4">
                  <div>
                    <Label className="text-gray-400 text-sm">Hero Titel</Label>
                    <Input 
                      value={siteSettings.landing?.heroTitle || ''}
                      onChange={(e) => updateSetting('landing', 'heroTitle', e.target.value)}
                      placeholder="Ella Gold"
                      className="bg-white/5 border-white/10 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-400 text-sm">Hero Subtitel</Label>
                    <Input 
                      value={siteSettings.landing?.heroSubtitle || ''}
                      onChange={(e) => updateSetting('landing', 'heroSubtitle', e.target.value)}
                      placeholder="Exclusive AI-Generated Digital Art"
                      className="bg-white/5 border-white/10 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-400 text-sm">Hero Beschrijving</Label>
                    <Textarea 
                      value={siteSettings.landing?.heroDescription || ''}
                      onChange={(e) => updateSetting('landing', 'heroDescription', e.target.value)}
                      placeholder="Discover our exclusive collection..."
                      className="bg-white/5 border-white/10 text-white mt-1 min-h-[80px]"
                    />
                  </div>
                </div>
              </div>

              {/* Social & Contact Settings */}
              <div className="glass rounded-xl p-6">
                <h3 className="font-medium mb-4 flex items-center gap-2">
                  <Settings className="w-4 h-4 text-[#D4AF37]" />
                  Social Media & Contact
                </h3>
                <div className="space-y-4">
                  <div>
                    <Label className="text-gray-400 text-sm">Instagram URL</Label>
                    <Input 
                      value={siteSettings.social?.instagram || ''}
                      onChange={(e) => updateSetting('social', 'instagram', e.target.value)}
                      placeholder="https://instagram.com/..."
                      className="bg-white/5 border-white/10 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-400 text-sm">X (Twitter) URL</Label>
                    <Input 
                      value={siteSettings.social?.twitter || ''}
                      onChange={(e) => updateSetting('social', 'twitter', e.target.value)}
                      placeholder="https://x.com/..."
                      className="bg-white/5 border-white/10 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-400 text-sm">TikTok URL</Label>
                    <Input 
                      value={siteSettings.social?.tiktok || ''}
                      onChange={(e) => updateSetting('social', 'tiktok', e.target.value)}
                      placeholder="https://tiktok.com/@..."
                      className="bg-white/5 border-white/10 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-400 text-sm">Discord Invite URL</Label>
                    <Input 
                      value={siteSettings.social?.discord || ''}
                      onChange={(e) => updateSetting('social', 'discord', e.target.value)}
                      placeholder="https://discord.gg/..."
                      className="bg-white/5 border-white/10 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-400 text-sm">Contact Email</Label>
                    <Input 
                      value={siteSettings.social?.email || ''}
                      onChange={(e) => updateSetting('social', 'email', e.target.value)}
                      placeholder="contact@ellagold.com"
                      className="bg-white/5 border-white/10 text-white mt-1"
                    />
                  </div>
                </div>
              </div>

              {/* Shop Settings */}
              <div className="glass rounded-xl p-6">
                <h3 className="font-medium mb-4 flex items-center gap-2">
                  <Settings className="w-4 h-4 text-[#D4AF37]" />
                  Shop Instellingen
                </h3>
                <div className="space-y-4">
                  <div>
                    <Label className="text-gray-400 text-sm">Winkel Titel</Label>
                    <Input 
                      value={siteSettings.shop?.title || ''}
                      onChange={(e) => updateSetting('shop', 'title', e.target.value)}
                      placeholder="Digital Art Shop"
                      className="bg-white/5 border-white/10 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-400 text-sm">Winkel Beschrijving</Label>
                    <Textarea 
                      value={siteSettings.shop?.description || ''}
                      onChange={(e) => updateSetting('shop', 'description', e.target.value)}
                      placeholder="Exclusive digital products..."
                      className="bg-white/5 border-white/10 text-white mt-1 min-h-[80px]"
                    />
                  </div>
                </div>
              </div>

              {/* Footer Settings */}
              <div className="glass rounded-xl p-6">
                <h3 className="font-medium mb-4 flex items-center gap-2">
                  <Settings className="w-4 h-4 text-[#D4AF37]" />
                  Footer Teksten
                </h3>
                <div className="space-y-4">
                  <div>
                    <Label className="text-gray-400 text-sm">Copyright Tekst</Label>
                    <Input 
                      value={siteSettings.footer?.copyright || ''}
                      onChange={(e) => updateSetting('footer', 'copyright', e.target.value)}
                      placeholder="© 2024 Ella Gold Official. All rights reserved. 18+"
                      className="bg-white/5 border-white/10 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-400 text-sm">Tagline</Label>
                    <Input 
                      value={siteSettings.footer?.tagline || ''}
                      onChange={(e) => updateSetting('footer', 'tagline', e.target.value)}
                      placeholder="Where art meets desire."
                      className="bg-white/5 border-white/10 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-400 text-sm">Disclaimer</Label>
                    <Textarea 
                      value={siteSettings.footer?.disclaimer || ''}
                      onChange={(e) => updateSetting('footer', 'disclaimer', e.target.value)}
                      placeholder="All content is AI-generated..."
                      className="bg-white/5 border-white/10 text-white mt-1 min-h-[80px]"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Save Button */}
            <div className="mt-6 flex justify-end">
              <Button 
                onClick={saveSettings}
                disabled={savingSettings}
                className="btn-gold px-8"
              >
                {savingSettings ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    Opslaan...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Save className="w-4 h-4" />
                    Instellingen Opslaan
                  </span>
                )}
              </Button>
            </div>
          </TabsContent>

          {/* ============== WEBSITE IMAGES TAB ============== */}
          <TabsContent value="images" className="mt-0">
            <div className="glass rounded-xl p-6 mb-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="font-medium flex items-center gap-2">
                    <Image className="w-5 h-5 text-[#D4AF37]" />
                    Website Afbeeldingen Beheren
                  </h3>
                  <p className="text-sm text-gray-400 mt-1">
                    Klik op een afbeelding om deze te wijzigen. Upload eerst afbeeldingen via de Media Uploaden tab.
                  </p>
                </div>
                <Button 
                  onClick={saveSiteImages}
                  disabled={savingImages}
                  className="btn-gold"
                >
                  {savingImages ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                      Opslaan...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Save className="w-4 h-4" />
                      Afbeeldingen Opslaan
                    </span>
                  )}
                </Button>
              </div>

              {/* Image Locations Grid */}
              <div className="space-y-8">
                {/* Landing Page Images */}
                <div>
                  <h4 className="text-sm font-medium text-[#D4AF37] mb-4">Landing Page</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                    {imageLocations.filter(l => l.section === 'Landing Page').map(loc => (
                      <div 
                        key={loc.id}
                        onClick={() => openImagePicker(loc.id)}
                        className="group cursor-pointer"
                      >
                        <div className="aspect-square rounded-xl overflow-hidden border-2 border-dashed border-white/20 hover:border-[#D4AF37] transition-colors bg-white/5 relative">
                          {siteImages[loc.id]?.image_url ? (
                            <img 
                              src={siteImages[loc.id].image_url}
                              alt={loc.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-500">
                              <Plus className="w-8 h-8 mb-2" />
                              <span className="text-xs">Kies afbeelding</span>
                            </div>
                          )}
                          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <Edit2 className="w-6 h-6 text-white" />
                          </div>
                        </div>
                        <p className="text-xs text-gray-400 mt-2 text-center">{loc.name}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Shop Images */}
                <div>
                  <h4 className="text-sm font-medium text-[#D4AF37] mb-4">Shop</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                    {imageLocations.filter(l => l.section === 'Shop').map(loc => (
                      <div 
                        key={loc.id}
                        onClick={() => openImagePicker(loc.id)}
                        className="group cursor-pointer"
                      >
                        <div className="aspect-square rounded-xl overflow-hidden border-2 border-dashed border-white/20 hover:border-[#D4AF37] transition-colors bg-white/5 relative">
                          {siteImages[loc.id]?.image_url ? (
                            <img 
                              src={siteImages[loc.id].image_url}
                              alt={loc.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-500">
                              <Plus className="w-8 h-8 mb-2" />
                              <span className="text-xs">Kies afbeelding</span>
                            </div>
                          )}
                          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <Edit2 className="w-6 h-6 text-white" />
                          </div>
                        </div>
                        <p className="text-xs text-gray-400 mt-2 text-center">{loc.name}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Gallery Page Images */}
                <div>
                  <h4 className="text-sm font-medium text-[#D4AF37] mb-4">Gallery Pagina</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
                    {imageLocations.filter(l => l.section === 'Gallery Page').map(loc => (
                      <div 
                        key={loc.id}
                        onClick={() => openImagePicker(loc.id)}
                        className="group cursor-pointer"
                      >
                        <div className="aspect-square rounded-xl overflow-hidden border-2 border-dashed border-white/20 hover:border-[#D4AF37] transition-colors bg-white/5 relative">
                          {siteImages[loc.id]?.image_url ? (
                            <img 
                              src={siteImages[loc.id].image_url}
                              alt={loc.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-500">
                              <Plus className="w-6 h-6 mb-1" />
                              <span className="text-[10px]">Kies</span>
                            </div>
                          )}
                          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <Edit2 className="w-5 h-5 text-white" />
                          </div>
                        </div>
                        <p className="text-[10px] text-gray-400 mt-1 text-center">{loc.name}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Auth Pages Images */}
                <div>
                  <h4 className="text-sm font-medium text-[#D4AF37] mb-4">Login & Registratie</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                    {imageLocations.filter(l => l.section === 'Auth').map(loc => (
                      <div 
                        key={loc.id}
                        onClick={() => openImagePicker(loc.id)}
                        className="group cursor-pointer"
                      >
                        <div className="aspect-square rounded-xl overflow-hidden border-2 border-dashed border-white/20 hover:border-[#D4AF37] transition-colors bg-white/5 relative">
                          {siteImages[loc.id]?.image_url ? (
                            <img 
                              src={siteImages[loc.id].image_url}
                              alt={loc.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-500">
                              <Plus className="w-8 h-8 mb-2" />
                              <span className="text-xs">Kies afbeelding</span>
                            </div>
                          )}
                          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <Edit2 className="w-6 h-6 text-white" />
                          </div>
                        </div>
                        <p className="text-xs text-gray-400 mt-2 text-center">{loc.name}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* ============== NEWS TAB ============== */}
          <TabsContent value="news" className="mt-0">
            <div className="glass rounded-xl p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-lg font-medium flex items-center gap-2">
                    <Newspaper className="w-5 h-5 text-[#D4AF37]" />
                    Nieuws Beheer
                  </h3>
                  <p className="text-sm text-gray-400 mt-1">
                    Maak en beheer nieuws artikelen voor je website.
                  </p>
                </div>
                <Button onClick={() => openNewsDialog()} className="btn-gold">
                  <Plus className="w-4 h-4 mr-2" />
                  Nieuw Artikel
                </Button>
              </div>

              {news.length === 0 ? (
                <div className="text-center py-16">
                  <Newspaper className="w-16 h-16 mx-auto text-gray-600 mb-4" />
                  <p className="text-gray-400">Nog geen nieuws artikelen</p>
                  <p className="text-sm text-gray-500 mt-2">Klik op &quot;Nieuw Artikel&quot; om te beginnen</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {news.map((article) => (
                    <div 
                      key={article.id}
                      className="flex items-center gap-4 p-4 bg-white/5 rounded-xl border border-white/10 hover:border-[#D4AF37]/30 transition-colors"
                    >
                      {/* Image */}
                      {article.image_url ? (
                        <div className="w-24 h-16 rounded-lg overflow-hidden flex-shrink-0">
                          <img 
                            src={article.image_url}
                            alt={article.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ) : (
                        <div className="w-24 h-16 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
                          <FileImage className="w-6 h-6 text-gray-600" />
                        </div>
                      )}

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-medium truncate">{article.title}</h4>
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            article.is_published 
                              ? 'bg-green-500/20 text-green-400' 
                              : 'bg-yellow-500/20 text-yellow-400'
                          }`}>
                            {article.is_published ? 'Gepubliceerd' : 'Concept'}
                          </span>
                          <span className="px-2 py-0.5 rounded text-xs bg-white/10 text-gray-400">
                            {article.category}
                          </span>
                        </div>
                        <p className="text-sm text-gray-400 truncate">{article.content}</p>
                        <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                          <Calendar className="w-3 h-3" />
                          {article.created_at ? new Date(article.created_at).toLocaleDateString('nl-NL') : 'Onbekend'}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2">
                        <Button
                          onClick={() => openNewsDialog(article)}
                          variant="ghost"
                          size="sm"
                          className="text-gray-400 hover:text-white"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          onClick={() => deleteNews(article.id)}
                          variant="ghost"
                          size="sm"
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* ============== SHOP PRODUCTS TAB ============== */}
          <TabsContent value="shop-products" className="mt-0">
            <div className="glass rounded-xl p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-lg font-medium flex items-center gap-2">
                    <ShoppingBag className="w-5 h-5 text-[#D4AF37]" />
                    Shop Producten Beheer
                  </h3>
                  <p className="text-sm text-gray-400 mt-1">
                    Beheer je shop producten, afbeeldingen en downloadbare bestanden (PDF&apos;s).
                  </p>
                </div>
                <Button onClick={() => {
                  setEditingShopProduct(null);
                  setShopProductForm({
                    id: '',
                    name: '',
                    description: '',
                    price: 0,
                    image_url: '',
                    file_url: '',
                    category: 'digital',
                    is_active: true
                  });
                  setShopProductDialog(true);
                }} className="btn-gold">
                  <Plus className="w-4 h-4 mr-2" />
                  Nieuw Product
                </Button>
              </div>

              {shopProducts.length === 0 ? (
                <div className="text-center py-16">
                  <ShoppingBag className="w-16 h-16 mx-auto text-gray-600 mb-4" />
                  <p className="text-gray-400">Nog geen shop producten</p>
                  <p className="text-sm text-gray-500 mt-2">Klik op &quot;Nieuw Product&quot; om te beginnen</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {shopProducts.map((product) => (
                    <div 
                      key={product.id}
                      className="p-4 bg-white/5 rounded-xl border border-white/10 hover:border-[#D4AF37]/30 transition-colors"
                    >
                      {/* Product Image */}
                      {product.image_url ? (
                        <div className="aspect-video rounded-lg overflow-hidden mb-4">
                          <img 
                            src={product.image_url}
                            alt={product.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ) : (
                        <div className="aspect-video rounded-lg bg-white/5 flex items-center justify-center mb-4">
                          <FileImage className="w-12 h-12 text-gray-600" />
                        </div>
                      )}

                      {/* Product Info */}
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium truncate">{product.name}</h4>
                          <p className="text-sm text-gray-400 truncate">{product.description}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <span className="text-lg font-bold text-[#D4AF37]">€{product.price}</span>
                            {product.file_url && (
                              <span className="px-2 py-0.5 rounded text-xs bg-green-500/20 text-green-400 flex items-center gap-1">
                                <FileText className="w-3 h-3" />
                                PDF
                              </span>
                            )}
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              product.is_active 
                                ? 'bg-green-500/20 text-green-400' 
                                : 'bg-red-500/20 text-red-400'
                            }`}>
                              {product.is_active ? 'Actief' : 'Inactief'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2 mt-4">
                        <Button
                          onClick={() => {
                            setEditingShopProduct(product);
                            setShopProductForm({
                              id: product.id,
                              name: product.name || '',
                              description: product.description || '',
                              price: product.price || 0,
                              image_url: product.image_url || '',
                              file_url: product.file_url || '',
                              category: product.category || 'digital',
                              is_active: product.is_active !== false
                            });
                            setShopProductDialog(true);
                          }}
                          variant="outline"
                          size="sm"
                          className="flex-1 border-white/10 text-white hover:bg-white/5"
                        >
                          <Edit2 className="w-4 h-4 mr-1" />
                          Bewerken
                        </Button>
                        <Button
                          onClick={async () => {
                            if (!window.confirm('Weet je zeker dat je dit product wilt verwijderen?')) return;
                            try {
                              const token = localStorage.getItem('token');
                              await axios.delete(`${API}/admin/shop-products/${product.id}`, {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              toast.success('Product verwijderd!');
                              fetchData();
                            } catch (error) {
                              toast.error('Kon product niet verwijderen');
                            }
                          }}
                          variant="ghost"
                          size="sm"
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* ============== EMAIL TAB ============== */}
          <TabsContent value="email" className="mt-0">
            <div className="glass rounded-xl p-6">
              <div className="flex items-center gap-3 mb-6">
                <Mail className="w-6 h-6 text-[#D4AF37]" />
                <div>
                  <h3 className="text-lg font-medium">Contact Email Instellingen</h3>
                  <p className="text-sm text-gray-400">
                    Stel het email adres in waar bezoekers contact mee kunnen opnemen.
                  </p>
                </div>
              </div>

              <div className="max-w-xl space-y-6">
                <div>
                  <Label className="text-gray-300">Contact Email Adres</Label>
                  <Input 
                    type="email"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    placeholder="contact@ellagold.com"
                    className="bg-white/5 border-white/10 text-white mt-2"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Dit email adres wordt getoond in de footer en contact pagina&apos;s.
                  </p>
                </div>

                <Button 
                  onClick={async () => {
                    setSavingEmail(true);
                    try {
                      await axios.post(`${API}/admin/settings/bulk`, [
                        { section: 'social', key: 'email', value: contactEmail }
                      ]);
                      toast.success('Email opgeslagen!');
                    } catch (error) {
                      toast.error('Kon email niet opslaan');
                    } finally {
                      setSavingEmail(false);
                    }
                  }}
                  disabled={savingEmail}
                  className="btn-gold"
                >
                  {savingEmail ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                      Opslaan...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Save className="w-4 h-4" />
                      Email Opslaan
                    </span>
                  )}
                </Button>

                {/* Email Display Preview */}
                <div className="p-4 bg-white/5 rounded-xl border border-white/10 mt-6">
                  <h4 className="text-sm font-medium text-gray-300 mb-2">Voorbeeld op je website:</h4>
                  <div className="flex items-center gap-2 text-[#D4AF37]">
                    <Mail className="w-4 h-4" />
                    <a href={`mailto:${contactEmail || 'contact@ellagold.com'}`} className="hover:underline">
                      {contactEmail || 'contact@ellagold.com'}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* ============== MEMBERS TAB ============== */}
          <TabsContent value="members" className="mt-0">
            <div className="glass rounded-xl p-6">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
                <div>
                  <h3 className="text-lg font-medium flex items-center gap-2">
                    <UserCheck className="w-5 h-5 text-[#D4AF37]" />
                    Leden Overzicht
                  </h3>
                  <p className="text-sm text-gray-400 mt-1">
                    Bekijk alle geregistreerde leden, hun email en aankopen.
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  {/* Admin Token Balance */}
                  <div className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg">
                    <Coins className="w-5 h-5 text-[#D4AF37]" />
                    <div>
                      <p className="text-xs text-gray-400">Jouw Tokens</p>
                      <p className="text-lg font-bold text-[#D4AF37]">{adminTokenBalance}</p>
                    </div>
                  </div>

                  {/* Auto News Toggle */}
                  <div className="flex items-center gap-2 px-3 py-2 bg-white/5 rounded-lg">
                    <span className="text-sm text-gray-400">Auto Nieuws:</span>
                    <button
                      onClick={async () => {
                        const newValue = !autoNewsEnabled;
                        setAutoNewsEnabled(newValue);
                        await axios.post(`${API}/admin/settings/bulk`, [
                          { section: 'news', key: 'auto_news_enabled', value: newValue ? 'true' : 'false' }
                        ]);
                        toast.success(newValue ? 'Auto nieuws ingeschakeld' : 'Auto nieuws uitgeschakeld');
                      }}
                      className={`w-10 h-5 rounded-full transition-colors ${
                        autoNewsEnabled ? 'bg-[#D4AF37]' : 'bg-white/20'
                      }`}
                    >
                      <div className={`w-4 h-4 rounded-full bg-white transform transition-transform ${
                        autoNewsEnabled ? 'translate-x-5' : 'translate-x-0.5'
                      }`} />
                    </button>
                  </div>
                  
                  {/* Search */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      value={memberSearch}
                      onChange={(e) => setMemberSearch(e.target.value)}
                      placeholder="Zoek op email of naam..."
                      className="pl-10 bg-white/5 border-white/10 w-64"
                    />
                  </div>
                </div>
              </div>

              {/* Stats Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                  <div className="flex items-center gap-2 text-gray-400 text-sm">
                    <Users className="w-4 h-4" />
                    Totaal Leden
                  </div>
                  <p className="text-2xl font-bold text-[#D4AF37] mt-1">{members.length}</p>
                </div>
                <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                  <div className="flex items-center gap-2 text-gray-400 text-sm">
                    <DollarSign className="w-4 h-4" />
                    Totale Omzet
                  </div>
                  <p className="text-2xl font-bold text-green-400 mt-1">
                    €{members.reduce((sum, m) => sum + (m.total_spent || 0), 0).toFixed(2)}
                  </p>
                </div>
                <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                  <div className="flex items-center gap-2 text-gray-400 text-sm">
                    <Package className="w-4 h-4" />
                    Totaal Aankopen
                  </div>
                  <p className="text-2xl font-bold text-blue-400 mt-1">
                    {members.reduce((sum, m) => sum + (m.purchase_count || 0), 0)}
                  </p>
                </div>
                <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                  <div className="flex items-center gap-2 text-gray-400 text-sm">
                    <Clock className="w-4 h-4" />
                    Nieuw Vandaag
                  </div>
                  <p className="text-2xl font-bold text-purple-400 mt-1">
                    {members.filter(m => {
                      const created = new Date(m.created_at);
                      const today = new Date();
                      return created.toDateString() === today.toDateString();
                    }).length}
                  </p>
                </div>
              </div>

              {/* Members Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-3 px-4 text-gray-400 font-medium">Lid</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium">Email</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium">Lid Sinds</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium">Tokens</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium">Aankopen</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium">Uitgegeven</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium">Tier</th>
                      <th className="text-right py-3 px-4 text-gray-400 font-medium">Acties</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members
                      .filter(m => 
                        !memberSearch || 
                        m.email?.toLowerCase().includes(memberSearch.toLowerCase()) ||
                        m.name?.toLowerCase().includes(memberSearch.toLowerCase())
                      )
                      .map((member) => (
                        <tr 
                          key={member.id} 
                          className="border-b border-white/5 hover:bg-white/5 transition-colors"
                        >
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#8B7025] flex items-center justify-center text-black font-bold text-sm">
                                {(member.name || member.email || '?')[0].toUpperCase()}
                              </div>
                              <span className="font-medium">{member.name || '-'}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-gray-300">{member.email}</td>
                          <td className="py-3 px-4 text-gray-400 text-sm">
                            {member.created_at ? new Date(member.created_at).toLocaleDateString('nl-NL') : '-'}
                          </td>
                          <td className="py-3 px-4">
                            <span className="px-2 py-1 rounded-full bg-[#D4AF37]/20 text-[#D4AF37] text-sm font-medium">
                              {member.credits || 0}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <span className="px-2 py-1 rounded-full bg-blue-500/20 text-blue-400 text-sm">
                              {member.purchase_count || 0}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-green-400 font-medium">
                            €{(member.total_spent || 0).toFixed(2)}
                          </td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              member.loyalty_tier === 'gold' ? 'bg-[#D4AF37]/20 text-[#D4AF37]' :
                              member.loyalty_tier === 'silver' ? 'bg-gray-400/20 text-gray-400' :
                              'bg-amber-700/20 text-amber-600'
                            }`}>
                              {member.loyalty_tier || 'bronze'}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                onClick={() => setSelectedMember(member)}
                                variant="ghost"
                                size="sm"
                                className="text-[#D4AF37] hover:text-[#D4AF37]/80"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              {!member.is_admin && (
                                <>
                                  <Button
                                    onClick={() => {
                                      setTokenTransferDialog(member);
                                      setTokenAmount(10);
                                      setTokenReason('');
                                    }}
                                    variant="ghost"
                                    size="sm"
                                    className="text-[#D4AF37] hover:text-[#D4AF37]/80"
                                    title="Stuur Tokens"
                                  >
                                    <Send className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    onClick={async () => {
                                      if (!window.confirm(`Weet je zeker dat je ${member.email} wilt verwijderen?`)) return;
                                      try {
                                        const token = localStorage.getItem('token');
                                        await axios.delete(`${API}/admin/members/${member.id}`, {
                                          headers: { Authorization: `Bearer ${token}` }
                                        });
                                        toast.success('Lid verwijderd');
                                        fetchData();
                                      } catch (error) {
                                        toast.error('Kon lid niet verwijderen');
                                      }
                                    }}
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-400 hover:text-red-300"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>

              {members.length === 0 && (
                <div className="text-center py-16">
                  <Users className="w-16 h-16 mx-auto text-gray-600 mb-4" />
                  <p className="text-gray-400">Nog geen leden geregistreerd</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* ============== VAULT CONFIG TAB ============== */}
          <TabsContent value="vault" className="mt-0">
            <div className="glass rounded-xl p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                  <Crown className="w-6 h-6 text-[#D4AF37]" />
                </div>
                <div>
                  <h2 className="text-xl font-medium">Secret Vault Configuratie</h2>
                  <p className="text-sm text-gray-400">Kies welke gallery de exclusieve vault is voor Diamond members</p>
                </div>
              </div>

              <div className="space-y-6 max-w-xl">
                <div>
                  <Label>Vault Naam</Label>
                  <Input
                    value={vaultConfig.name}
                    onChange={(e) => setVaultConfig({ ...vaultConfig, name: e.target.value })}
                    placeholder="Secret Vault"
                    className="mt-2 bg-white/5 border-white/10"
                  />
                </div>

                <div>
                  <Label>Beschrijving</Label>
                  <Textarea
                    value={vaultConfig.description || ''}
                    onChange={(e) => setVaultConfig({ ...vaultConfig, description: e.target.value })}
                    placeholder="Exclusieve content alleen voor Diamond members..."
                    className="mt-2 bg-white/5 border-white/10"
                  />
                </div>

                <div>
                  <Label>Gallery/Map Selecteren</Label>
                  <Select 
                    value={vaultConfig.gallery_id} 
                    onValueChange={(val) => setVaultConfig({ ...vaultConfig, gallery_id: val })}
                  >
                    <SelectTrigger className="mt-2 bg-white/5 border-white/10">
                      <SelectValue placeholder="Selecteer een gallery..." />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10">
                      {folders.map(folder => (
                        <SelectItem key={folder.id} value={folder.id}>{folder.name}</SelectItem>
                      ))}
                      <SelectItem value="vault">vault</SelectItem>
                      <SelectItem value="premium">premium</SelectItem>
                      <SelectItem value="diamond_content">diamond_content</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Minimaal Lidmaatschap</Label>
                  <Select 
                    value={vaultConfig.min_tier} 
                    onValueChange={(val) => setVaultConfig({ ...vaultConfig, min_tier: val })}
                  >
                    <SelectTrigger className="mt-2 bg-white/5 border-white/10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10">
                      <SelectItem value="bronze">Bronze (iedereen)</SelectItem>
                      <SelectItem value="silver">Silver</SelectItem>
                      <SelectItem value="gold">Gold</SelectItem>
                      <SelectItem value="diamond">Diamond (exclusief)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button
                  onClick={async () => {
                    setSavingVault(true);
                    try {
                      const token = localStorage.getItem('token');
                      await axios.post(`${API}/admin/vault/config`, vaultConfig, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      toast.success('Vault configuratie opgeslagen!');
                    } catch (error) {
                      toast.error('Fout bij opslaan vault configuratie');
                    } finally {
                      setSavingVault(false);
                    }
                  }}
                  disabled={savingVault || !vaultConfig.gallery_id}
                  className="w-full bg-[#D4AF37] hover:bg-[#B8963A] text-black"
                >
                  {savingVault ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Vault Configuratie Opslaan
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* ============== ELLA CHAT CONFIG TAB ============== */}
          <TabsContent value="ella-chat" className="mt-0">
            <div className="glass rounded-xl p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-[#D4AF37]" />
                </div>
                <div>
                  <h2 className="text-xl font-medium">Ella Chat Configuratie</h2>
                  <p className="text-sm text-gray-400">Beheer welke foto&apos;s Ella stuurt in de Gold Members chat</p>
                </div>
              </div>

              <div className="space-y-6">
                {/* Current Images */}
                <div>
                  <Label className="mb-3 block">Huidige Ella Foto&apos;s (klik om te verwijderen)</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {ellaChatImages.map((img, idx) => (
                      <div 
                        key={idx}
                        className="relative aspect-square rounded-xl overflow-hidden border border-white/10 group cursor-pointer"
                        onClick={() => {
                          setEllaChatImages(ellaChatImages.filter((_, i) => i !== idx));
                        }}
                      >
                        <img src={img} alt={`Ella ${idx + 1}`} className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-red-500/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <Trash2 className="w-8 h-8 text-white" />
                        </div>
                      </div>
                    ))}
                    {ellaChatImages.length === 0 && (
                      <div className="col-span-4 text-center py-8 text-gray-400">
                        Nog geen foto&apos;s toegevoegd. Voeg hieronder foto&apos;s toe.
                      </div>
                    )}
                  </div>
                </div>

                {/* Add New Image */}
                <div>
                  <Label>Nieuwe Foto Toevoegen (URL)</Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      value={newEllaChatImage}
                      onChange={(e) => setNewEllaChatImage(e.target.value)}
                      placeholder="https://example.com/image.jpg"
                      className="flex-1 bg-white/5 border-white/10"
                    />
                    <Button
                      onClick={() => {
                        if (newEllaChatImage.trim()) {
                          setEllaChatImages([...ellaChatImages, newEllaChatImage.trim()]);
                          setNewEllaChatImage('');
                        }
                      }}
                      disabled={!newEllaChatImage.trim()}
                      className="bg-[#D4AF37] hover:bg-[#B8963A] text-black"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Tip: Upload foto&apos;s eerst via Media Uploaden en kopieer dan de URL
                  </p>
                </div>

                {/* Upload from Media */}
                <div className="p-4 bg-white/5 rounded-xl">
                  <h4 className="font-medium mb-2">Of selecteer uit geüploade media</h4>
                  <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 max-h-48 overflow-y-auto">
                    {media.filter(m => m.content_type?.startsWith('image/')).slice(0, 24).map(mediaItem => (
                      <div 
                        key={mediaItem.id}
                        className="aspect-square rounded cursor-pointer border-2 border-transparent hover:border-[#D4AF37] transition-colors overflow-hidden"
                        onClick={() => {
                          const url = `${process.env.REACT_APP_BACKEND_URL}/api/media/${mediaItem.id}`;
                          if (!ellaChatImages.includes(url)) {
                            setEllaChatImages([...ellaChatImages, url]);
                          }
                        }}
                      >
                        <img 
                          src={`${process.env.REACT_APP_BACKEND_URL}/api/media/${mediaItem.id}`}
                          alt={mediaItem.filename}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <Button
                  onClick={async () => {
                    setSavingEllaConfig(true);
                    try {
                      const token = localStorage.getItem('token');
                      await axios.post(`${API}/admin/ella-config`, {
                        images: ellaChatImages,
                        profile_image: ellaChatImages[0] || ''
                      }, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      toast.success('Ella configuratie opgeslagen!');
                    } catch (error) {
                      toast.error('Fout bij opslaan Ella configuratie');
                    } finally {
                      setSavingEllaConfig(false);
                    }
                  }}
                  disabled={savingEllaConfig}
                  className="w-full bg-[#D4AF37] hover:bg-[#B8963A] text-black"
                >
                  {savingEllaConfig ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Ella Configuratie Opslaan
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* ============== PACKAGES MANAGEMENT TAB ============== */}
          <TabsContent value="packages-manage" className="mt-0">
            <div className="glass rounded-xl p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-lg font-medium flex items-center gap-2">
                    <Package className="w-5 h-5 text-[#D4AF37]" />
                    Packages & Abonnementen Beheer
                  </h3>
                  <p className="text-sm text-gray-400 mt-1">
                    Beheer al je packages, lidmaatschappen en bundles. Koppel content uit je gallery.
                  </p>
                </div>
                <Button onClick={() => {
                  setEditingPackage(null);
                  setPackageForm({
                    id: '',
                    name: '',
                    description: '',
                    token_price: 0,
                    euro_price: 0,
                    image_url: '',
                    gallery_folder: '',
                    features: [],
                    is_active: true,
                    type: 'subscription'
                  });
                  setPackageDialog(true);
                }} className="btn-gold">
                  <Plus className="w-4 h-4 mr-2" />
                  Nieuw Package
                </Button>
              </div>

              {/* Package Types */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-xl">
                  <h4 className="font-medium text-purple-400 mb-2">Abonnementen</h4>
                  <p className="text-sm text-gray-400">Maandelijkse of jaarlijkse toegang</p>
                </div>
                <div className="p-4 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-xl">
                  <h4 className="font-medium text-[#D4AF37] mb-2">Lifetime Access</h4>
                  <p className="text-sm text-gray-400">Eenmalige betaling, eeuwige toegang</p>
                </div>
                <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
                  <h4 className="font-medium text-blue-400 mb-2">Bundles</h4>
                  <p className="text-sm text-gray-400">Speciale combinatie pakketten</p>
                </div>
              </div>

              {/* Current Packages */}
              <div className="space-y-4">
                <h4 className="font-medium text-gray-300">Huidige Packages</h4>
                
                {/* Hardcoded packages that can be customized */}
                {[
                  { id: 'daily_tease', name: 'Daily Tease', type: 'subscription', token_price: 6, description: 'Dagelijkse content', icon: '☕' },
                  { id: 'weekly_obsession', name: 'Weekly Obsession', type: 'subscription', token_price: 10, description: 'Wekelijkse premium content', icon: '💎' },
                  { id: 'monthly_devotion', name: 'Monthly Devotion', type: 'subscription', token_price: 15, description: 'Maandelijkse exclusieve content', icon: '🔥' },
                  { id: 'pure_obsession', name: 'Pure Obsession', type: 'subscription', token_price: 30, description: 'Volledige toegang', icon: '👑' },
                  { id: 'lifetime_access', name: 'Lifetime Access', type: 'lifetime', token_price: 80, description: 'Eeuwige toegang tot alles', icon: '🏆' },
                  { id: 'gold_vip', name: 'Gold VIP Bundle', type: 'bundle', token_price: 50, description: 'Exclusive VIP bundle', icon: '⭐' },
                ].map((pkg) => (
                  <div 
                    key={pkg.id}
                    className="p-4 bg-white/5 rounded-xl border border-white/10 flex items-center justify-between"
                  >
                    <div className="flex items-center gap-4">
                      <span className="text-3xl">{pkg.icon}</span>
                      <div>
                        <h5 className="font-medium">{pkg.name}</h5>
                        <p className="text-sm text-gray-400">{pkg.description}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-[#D4AF37] font-bold">{pkg.token_price} tokens</span>
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            pkg.type === 'subscription' ? 'bg-purple-500/20 text-purple-400' :
                            pkg.type === 'lifetime' ? 'bg-[#D4AF37]/20 text-[#D4AF37]' :
                            'bg-blue-500/20 text-blue-400'
                          }`}>
                            {pkg.type}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setEditingPackage(pkg);
                          setPackageForm({
                            ...pkg,
                            features: pkg.features || [],
                            gallery_folder: pkg.gallery_folder || '',
                            image_url: pkg.image_url || '',
                            euro_price: pkg.euro_price || 0,
                            is_active: true
                          });
                          setPackageDialog(true);
                        }}
                        className="border-white/10"
                      >
                        <Edit2 className="w-4 h-4 mr-1" />
                        Bewerken
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Gallery Folders Info */}
              <div className="mt-8 p-4 bg-white/5 rounded-xl border border-white/10">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <FolderOpen className="w-4 h-4 text-[#D4AF37]" />
                  Beschikbare Gallery Folders
                </h4>
                <p className="text-sm text-gray-400 mb-3">
                  Koppel een folder aan een package. Bij aankoop krijgt de member automatisch toegang tot deze content.
                </p>
                <div className="flex flex-wrap gap-2">
                  {folders.map(folder => (
                    <span 
                      key={folder.id} 
                      className="px-3 py-1 bg-[#D4AF37]/20 text-[#D4AF37] rounded-full text-sm"
                    >
                      {folder.name} ({folder.count || 0} items)
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* ============== ANALYTICS TAB ============== */}
          <TabsContent value="analytics" className="mt-0">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Revenue Overview */}
              <div className="glass rounded-xl p-6">
                <h3 className="font-medium mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-[#D4AF37]" />
                  Omzet Overzicht
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
                    <span className="text-gray-400">Totale Omzet</span>
                    <span className="text-2xl font-bold text-[#D4AF37]">€{stats?.total_revenue?.toFixed(2) || '0.00'}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
                    <span className="text-gray-400">Gemiddelde Orderwaarde</span>
                    <span className="text-lg font-medium">€{stats?.total_purchases > 0 ? (stats?.total_revenue / stats?.total_purchases).toFixed(2) : '0.00'}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
                    <span className="text-gray-400">Conversie Ratio</span>
                    <span className="text-lg font-medium">{stats?.total_users > 0 ? ((stats?.total_purchases / stats?.total_users) * 100).toFixed(1) : '0'}%</span>
                  </div>
                </div>
              </div>

              {/* User Stats */}
              <div className="glass rounded-xl p-6">
                <h3 className="font-medium mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#D4AF37]" />
                  Gebruikers Statistieken
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
                    <span className="text-gray-400">Totaal Geregistreerd</span>
                    <span className="text-2xl font-bold">{stats?.total_users || 0}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
                    <span className="text-gray-400">Actieve Kopers</span>
                    <span className="text-lg font-medium">{stats?.active_buyers || stats?.total_purchases || 0}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
                    <span className="text-gray-400">Terugkerende Klanten</span>
                    <span className="text-lg font-medium text-green-400">{stats?.returning_customers || 0}%</span>
                  </div>
                </div>
              </div>

              {/* Content Stats */}
              <div className="glass rounded-xl p-6">
                <h3 className="font-medium mb-4 flex items-center gap-2">
                  <Image className="w-5 h-5 text-[#D4AF37]" />
                  Content Statistieken
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
                    <span className="text-gray-400">Totaal Media Items</span>
                    <span className="text-2xl font-bold">{media.length}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
                    <span className="text-gray-400">Pakketten</span>
                    <span className="text-lg font-medium">{stats?.total_packages || 0}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
                    <span className="text-gray-400">Shop Producten</span>
                    <span className="text-lg font-medium">{stats?.total_products || 0}</span>
                  </div>
                </div>
              </div>

              {/* Token Economy */}
              <div className="glass rounded-xl p-6">
                <h3 className="font-medium mb-4 flex items-center gap-2">
                  <Coins className="w-5 h-5 text-[#D4AF37]" />
                  Token Economie
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
                    <span className="text-gray-400">Admin Tokens</span>
                    <span className="text-2xl font-bold text-[#D4AF37]">{adminTokenBalance.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
                    <span className="text-gray-400">Tokens in Omloop</span>
                    <span className="text-lg font-medium">{stats?.tokens_in_circulation?.toLocaleString() || 0}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
                    <span className="text-gray-400">Tokens Verkocht</span>
                    <span className="text-lg font-medium">{stats?.tokens_sold?.toLocaleString() || 0}</span>
                  </div>
                </div>
              </div>

              {/* Top Performing Content */}
              <div className="glass rounded-xl p-6 lg:col-span-2">
                <h3 className="font-medium mb-4 flex items-center gap-2">
                  <Crown className="w-5 h-5 text-[#D4AF37]" />
                  Loyalty Levels - Bestedingsdrempels
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {[
                    { name: 'Bronze', tier: 'bronze', min: '€0', perks: 'Basis toegang', color: 'bg-amber-600' },
                    { name: 'Zilver', tier: 'silver', min: '€15+', perks: '5% korting', color: 'bg-gray-400' },
                    { name: 'Goud', tier: 'gold', min: '€30+', perks: '10% + Discord + VIP Chat', color: 'bg-[#D4AF37]' },
                    { name: 'Diamant', tier: 'diamond', min: '€100+', perks: '15% + Secret Vault', color: 'bg-cyan-400' }
                  ].map((level, i) => (
                    <div key={i} className="p-4 bg-white/5 rounded-lg border border-white/10">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`w-3 h-3 rounded-full ${level.color}`} />
                        <span className="font-medium">{level.name}</span>
                      </div>
                      <p className="text-2xl font-bold">{level.min}</p>
                      <p className="text-sm text-gray-400 mt-1">{level.perks}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* ============== NEWS DIALOG ============== */}
      <Dialog open={newsDialog} onOpenChange={setNewsDialog}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Newspaper className="w-5 h-5 text-[#D4AF37]" />
              {editingNews ? 'Artikel Bewerken' : 'Nieuw Artikel'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Instagram Import Section */}
            <div className="p-4 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-xl border border-purple-500/20">
              <div className="flex items-center gap-2 mb-3">
                <svg className="w-5 h-5 text-pink-400" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
                <span className="font-medium text-pink-400">Instagram Import</span>
              </div>
              <p className="text-sm text-gray-400 mb-3">
                Plak een Instagram post URL om automatisch de inhoud op te halen
              </p>
              <div className="flex gap-2">
                <Input
                  value={instagramInput}
                  onChange={(e) => setInstagramInput(e.target.value)}
                  placeholder="https://instagram.com/p/ABC123..."
                  className="flex-1 bg-white/5 border-white/10"
                />
                <Button
                  onClick={fetchInstagramPost}
                  disabled={fetchingInstagram}
                  className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
                >
                  {fetchingInstagram ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-1" />
                      Ophalen
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Title EN */}
            <div>
              <Label>Titel (Engels)</Label>
              <Input
                value={newsForm.title}
                onChange={(e) => setNewsForm({...newsForm, title: e.target.value})}
                placeholder="Article title in English..."
                className="mt-1 bg-white/5 border-white/10"
              />
            </div>

            {/* Title NL */}
            <div>
              <Label>Titel (Nederlands)</Label>
              <Input
                value={newsForm.title_nl}
                onChange={(e) => setNewsForm({...newsForm, title_nl: e.target.value})}
                placeholder="Artikel titel in het Nederlands..."
                className="mt-1 bg-white/5 border-white/10"
              />
            </div>

            {/* Content EN */}
            <div>
              <Label>Inhoud (Engels)</Label>
              <Textarea
                value={newsForm.content}
                onChange={(e) => setNewsForm({...newsForm, content: e.target.value})}
                placeholder="Write your article content in English..."
                rows={5}
                className="mt-1 bg-white/5 border-white/10"
              />
            </div>

            {/* Content NL */}
            <div>
              <Label>Inhoud (Nederlands)</Label>
              <Textarea
                value={newsForm.content_nl}
                onChange={(e) => setNewsForm({...newsForm, content_nl: e.target.value})}
                placeholder="Schrijf je artikel inhoud in het Nederlands..."
                rows={5}
                className="mt-1 bg-white/5 border-white/10"
              />
            </div>

            {/* Image URL */}
            <div>
              <Label>Afbeelding URL</Label>
              <Input
                value={newsForm.image_url}
                onChange={(e) => setNewsForm({...newsForm, image_url: e.target.value})}
                placeholder="https://..."
                className="mt-1 bg-white/5 border-white/10"
              />
            </div>

            {/* Category */}
            <div>
              <Label>Categorie</Label>
              <Select 
                value={newsForm.category} 
                onValueChange={(v) => setNewsForm({...newsForm, category: v})}
              >
                <SelectTrigger className="mt-1 bg-white/5 border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10">
                  <SelectItem value="news">Nieuws</SelectItem>
                  <SelectItem value="update">Update</SelectItem>
                  <SelectItem value="announcement">Aankondiging</SelectItem>
                  <SelectItem value="instagram">📸 Instagram</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Instagram URL (shown when category is instagram) */}
            {newsForm.category === 'instagram' && newsForm.instagram_url && (
              <div className="p-3 bg-purple-500/10 rounded-lg border border-purple-500/20">
                <p className="text-sm text-purple-300 flex items-center gap-2">
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073z"/>
                  </svg>
                  Gelinkt aan Instagram post
                </p>
                <a 
                  href={newsForm.instagram_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-purple-400 hover:text-purple-300 underline mt-1 block truncate"
                >
                  {newsForm.instagram_url}
                </a>
              </div>
            )}

            {/* Published */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="is_published"
                checked={newsForm.is_published}
                onChange={(e) => setNewsForm({...newsForm, is_published: e.target.checked})}
                className="w-4 h-4 rounded border-white/20 bg-white/5"
              />
              <Label htmlFor="is_published" className="cursor-pointer">
                Direct publiceren
              </Label>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
              <Button
                variant="ghost"
                onClick={() => setNewsDialog(false)}
                className="text-gray-400"
              >
                Annuleren
              </Button>
              <Button
                onClick={saveNews}
                disabled={savingNews}
                className="btn-gold"
              >
                {savingNews ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Opslaan...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Opslaan
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ============== IMAGE PICKER DIALOG ============== */}
      <Dialog open={imagePickerDialog} onOpenChange={setImagePickerDialog}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Image className="w-5 h-5 text-[#D4AF37]" />
              Kies een afbeelding voor: {imageLocations.find(l => l.id === currentImageLocation)?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <p className="text-sm text-gray-400 mb-4">
              Selecteer een afbeelding uit je geüploade media. Nog geen afbeeldingen? Ga naar de Media Uploaden tab.
            </p>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3 max-h-[50vh] overflow-y-auto">
              {media.map((item) => (
                <div 
                  key={item.id}
                  onClick={() => selectImageForLocation(getMediaUrl(item))}
                  className="aspect-square rounded-lg overflow-hidden cursor-pointer hover:ring-2 hover:ring-[#D4AF37] transition-all"
                >
                  <img 
                    src={getMediaUrl(item)}
                    alt={item.filename}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
              {media.length === 0 && (
                <div className="col-span-full text-center py-12 text-gray-500">
                  <Upload className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No media found</p>
                  <p className="text-sm mt-2">Upload eerst afbeeldingen via Media Uploaden</p>
                </div>
              )}
            </div>
            
            {/* Or enter URL manually */}
            <div className="mt-6 pt-4 border-t border-white/10">
              <Label className="text-gray-400 text-sm">Of voer een URL in:</Label>
              <div className="flex gap-2 mt-2">
                <Input 
                  placeholder="https://..."
                  className="bg-white/5 border-white/10 text-white flex-1"
                  id="manual-image-url"
                />
                <Button 
                  onClick={() => {
                    const input = document.getElementById('manual-image-url');
                    if (input?.value) {
                      selectImageForLocation(input.value);
                    }
                  }}
                  className="btn-gold"
                >
                  Gebruiken
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ============== ADD CONTENT DIALOG ============== */}
      <Dialog open={contentDialog} onOpenChange={setContentDialog}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Nieuwe Content</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {newContent.image_url && (
              <div className="aspect-video rounded-lg overflow-hidden bg-white/5">
                <img 
                  src={newContent.image_url}
                  alt="Preview"
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            <div>
              <Label className="text-gray-300">Titel</Label>
              <Input 
                value={newContent.title}
                onChange={(e) => setNewContent({...newContent, title: e.target.value})}
                className="bg-white/5 border-white/10 text-white mt-1"
                placeholder="Naam van de content"
              />
            </div>
            <div>
              <Label className="text-gray-300">Beschrijving</Label>
              <Input 
                value={newContent.description}
                onChange={(e) => setNewContent({...newContent, description: e.target.value})}
                className="bg-white/5 border-white/10 text-white mt-1"
                placeholder="Korte beschrijving"
              />
            </div>
            <div>
              <Label className="text-gray-300">Afbeelding URL</Label>
              <Input 
                value={newContent.image_url}
                onChange={(e) => setNewContent({...newContent, image_url: e.target.value, preview_url: e.target.value})}
                className="bg-white/5 border-white/10 text-white mt-1"
                placeholder="https://..."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">Type</Label>
                <Select value={newContent.content_type} onValueChange={(v) => setNewContent({...newContent, content_type: v})}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-white/10">
                    <SelectItem value="photo">Foto</SelectItem>
                    <SelectItem value="video">Video</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-gray-300">Tier</Label>
                <Select value={newContent.price_tier} onValueChange={(v) => setNewContent({...newContent, price_tier: v})}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-white/10">
                    <SelectItem value="bronze">Bronze</SelectItem>
                    <SelectItem value="silver">Silver</SelectItem>
                    <SelectItem value="gold">Gold</SelectItem>
                    <SelectItem value="platinum">Platinum</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button 
              onClick={handleCreateContent}
              className="w-full btn-gold"
            >
              <Save className="w-4 h-4 mr-2" />
              Opslaan
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ============== SHOP PRODUCT DIALOG ============== */}
      <Dialog open={shopProductDialog} onOpenChange={setShopProductDialog}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <ShoppingBag className="w-5 h-5 text-[#D4AF37]" />
              {editingShopProduct ? 'Product Bewerken' : 'Nieuw Product'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Product Image Preview */}
            {shopProductForm.image_url && (
              <div className="aspect-video rounded-lg overflow-hidden bg-white/5">
                <img 
                  src={shopProductForm.image_url}
                  alt="Preview"
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            {/* Product Name */}
            <div>
              <Label>Product Naam</Label>
              <Input
                value={shopProductForm.name}
                onChange={(e) => setShopProductForm({...shopProductForm, name: e.target.value})}
                placeholder="Bijv: Ultimate Prompt Guide"
                className="mt-1 bg-white/5 border-white/10"
              />
            </div>

            {/* Description */}
            <div>
              <Label>Beschrijving</Label>
              <Textarea
                value={shopProductForm.description}
                onChange={(e) => setShopProductForm({...shopProductForm, description: e.target.value})}
                placeholder="Beschrijf je product..."
                rows={3}
                className="mt-1 bg-white/5 border-white/10"
              />
            </div>

            {/* Price */}
            <div>
              <Label>Prijs (€)</Label>
              <div className="relative mt-1">
                <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={shopProductForm.price}
                  onChange={(e) => setShopProductForm({...shopProductForm, price: parseFloat(e.target.value) || 0})}
                  placeholder="49.00"
                  className="bg-white/5 border-white/10 pl-10"
                />
              </div>
            </div>

            {/* Image Selection */}
            <div>
              <Label>Product Afbeelding</Label>
              <p className="text-xs text-gray-500 mb-2">Kies uit je media of voer een URL in</p>
              <div className="grid grid-cols-4 gap-2 max-h-32 overflow-y-auto mb-2">
                {media.slice(0, 8).map((item) => (
                  <div 
                    key={item.id}
                    onClick={() => setShopProductForm({...shopProductForm, image_url: getMediaUrl(item)})}
                    className={`aspect-square rounded-lg overflow-hidden cursor-pointer border-2 transition-all ${
                      shopProductForm.image_url === getMediaUrl(item) 
                        ? 'border-[#D4AF37]' 
                        : 'border-transparent hover:border-white/30'
                    }`}
                  >
                    <img 
                      src={getMediaUrl(item)}
                      alt={item.filename}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
              <Input
                value={shopProductForm.image_url}
                onChange={(e) => setShopProductForm({...shopProductForm, image_url: e.target.value})}
                placeholder="Of plak een afbeelding URL..."
                className="bg-white/5 border-white/10"
              />
            </div>

            {/* PDF/File URL */}
            <div>
              <Label className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-[#D4AF37]" />
                Download Bestand (PDF/ZIP)
              </Label>
              <p className="text-xs text-gray-500 mb-2">URL naar het bestand dat klanten kunnen downloaden na aankoop</p>
              <Input
                value={shopProductForm.file_url}
                onChange={(e) => setShopProductForm({...shopProductForm, file_url: e.target.value})}
                placeholder="https://drive.google.com/... of directe link naar bestand"
                className="bg-white/5 border-white/10"
              />
            </div>

            {/* Category */}
            <div>
              <Label>Categorie</Label>
              <Select 
                value={shopProductForm.category} 
                onValueChange={(v) => setShopProductForm({...shopProductForm, category: v})}
              >
                <SelectTrigger className="mt-1 bg-white/5 border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10">
                  <SelectItem value="digital">Digitaal (PDF, Downloads)</SelectItem>
                  <SelectItem value="merch">Merchandise</SelectItem>
                  <SelectItem value="custom">Custom / Op Maat</SelectItem>
                  <SelectItem value="audio">Audio</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Active Toggle */}
            <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
              <div>
                <p className="font-medium">Product Actief</p>
                <p className="text-xs text-gray-400">Toon dit product in de shop</p>
              </div>
              <button
                onClick={() => setShopProductForm({...shopProductForm, is_active: !shopProductForm.is_active})}
                className={`w-12 h-6 rounded-full transition-colors ${
                  shopProductForm.is_active ? 'bg-[#D4AF37]' : 'bg-white/20'
                }`}
              >
                <div className={`w-5 h-5 rounded-full bg-white transform transition-transform ${
                  shopProductForm.is_active ? 'translate-x-6' : 'translate-x-0.5'
                }`} />
              </button>
            </div>

            {/* Save Button */}
            <Button 
              onClick={async () => {
                if (!shopProductForm.name || shopProductForm.price <= 0) {
                  toast.error('Naam en prijs zijn verplicht');
                  return;
                }
                setSavingShopProducts(true);
                try {
                  const token = localStorage.getItem('token');
                  if (editingShopProduct) {
                    await axios.put(`${API}/admin/shop-products/${editingShopProduct.id}`, shopProductForm, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    toast.success('Product bijgewerkt!');
                  } else {
                    await axios.post(`${API}/admin/shop-products`, shopProductForm, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    toast.success('Product aangemaakt!');
                  }
                  setShopProductDialog(false);
                  fetchData();
                } catch (error) {
                  toast.error('Kon product niet opslaan');
                } finally {
                  setSavingShopProducts(false);
                }
              }}
              disabled={savingShopProducts}
              className="w-full btn-gold"
            >
              {savingShopProducts ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Opslaan...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Product Opslaan
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ============== MEMBER DETAILS DIALOG ============== */}
      <Dialog open={!!selectedMember} onOpenChange={() => setSelectedMember(null)}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-[#D4AF37]" />
              Lid Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedMember && (
            <div className="space-y-6 mt-4">
              {/* Member Info */}
              <div className="flex items-start gap-4 p-4 bg-white/5 rounded-xl">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#8B7025] flex items-center justify-center text-black font-bold text-2xl">
                  {(selectedMember.name || selectedMember.email || '?')[0].toUpperCase()}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold">{selectedMember.name || 'Geen naam'}</h3>
                  <p className="text-gray-400">{selectedMember.email}</p>
                  <div className="flex items-center gap-4 mt-2">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      selectedMember.loyalty_tier === 'gold' ? 'bg-[#D4AF37]/20 text-[#D4AF37]' :
                      selectedMember.loyalty_tier === 'silver' ? 'bg-gray-400/20 text-gray-400' :
                      'bg-amber-700/20 text-amber-600'
                    }`}>
                      {selectedMember.loyalty_tier || 'bronze'} tier
                    </span>
                    <span className="text-sm text-gray-400">
                      Lid sinds: {selectedMember.created_at ? new Date(selectedMember.created_at).toLocaleDateString('nl-NL') : '-'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-white/5 rounded-xl">
                  <p className="text-gray-400 text-sm">Totaal Uitgegeven</p>
                  <p className="text-2xl font-bold text-green-400">€{(selectedMember.total_spent || 0).toFixed(2)}</p>
                </div>
                <div className="p-4 bg-white/5 rounded-xl">
                  <p className="text-gray-400 text-sm">Aantal Aankopen</p>
                  <p className="text-2xl font-bold text-blue-400">{selectedMember.purchase_count || 0}</p>
                </div>
              </div>

              {/* Purchase History */}
              <div>
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Package className="w-4 h-4 text-[#D4AF37]" />
                  Aankoop Geschiedenis
                </h4>
                {selectedMember.transactions && selectedMember.transactions.length > 0 ? (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {selectedMember.transactions.map((tx, idx) => (
                      <div key={idx} className="p-3 bg-white/5 rounded-lg flex items-center justify-between">
                        <div>
                          <p className="font-medium">{tx.package_id || 'Onbekend product'}</p>
                          <p className="text-xs text-gray-400">
                            {tx.created_at ? new Date(tx.created_at).toLocaleString('nl-NL') : '-'}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-green-400">€{(tx.amount || 0).toFixed(2)}</p>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            tx.payment_status === 'paid' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                          }`}>
                            {tx.payment_status || 'pending'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm p-4 bg-white/5 rounded-lg">Geen aankopen gevonden</p>
                )}
              </div>

              {/* Active Purchases */}
              {selectedMember.purchases && selectedMember.purchases.length > 0 && (
                <div>
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <Crown className="w-4 h-4 text-[#D4AF37]" />
                    Actieve Toegang
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedMember.purchases.map((p, idx) => (
                      <span key={idx} className="px-3 py-1 bg-[#D4AF37]/20 text-[#D4AF37] rounded-full text-sm">
                        {p.package_id}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* IP Adressen */}
              {selectedMember.ip_addresses && selectedMember.ip_addresses.length > 0 && (
                <div>
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <Eye className="w-4 h-4 text-[#D4AF37]" />
                    IP Adressen (Login History)
                  </h4>
                  <div className="p-3 bg-white/5 rounded-lg">
                    <div className="flex flex-wrap gap-2">
                      {selectedMember.ip_addresses.map((ip, idx) => (
                        <span key={idx} className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full text-xs font-mono">
                          {ip}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Laatste Login */}
              {selectedMember.last_login && (
                <div className="p-3 bg-white/5 rounded-lg">
                  <p className="text-gray-400 text-sm">Laatste Login</p>
                  <p className="text-white">{new Date(selectedMember.last_login).toLocaleString('nl-NL')}</p>
                </div>
              )}

              {/* Referred By */}
              {selectedMember.referred_by && (
                <div className="p-3 bg-green-500/10 rounded-lg border border-green-500/30">
                  <p className="text-gray-400 text-sm">Verwezen door</p>
                  <p className="text-green-400">{selectedMember.referred_by}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ============== TOKEN TRANSFER DIALOG ============== */}
      <Dialog open={!!tokenTransferDialog} onOpenChange={() => setTokenTransferDialog(null)}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Send className="w-5 h-5 text-[#D4AF37]" />
              Tokens Versturen
            </DialogTitle>
          </DialogHeader>
          
          {tokenTransferDialog && (
            <div className="space-y-4 mt-4">
              {/* Recipient Info */}
              <div className="p-4 bg-white/5 rounded-xl flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#8B7025] flex items-center justify-center text-black font-bold">
                  {(tokenTransferDialog.name || tokenTransferDialog.email || '?')[0].toUpperCase()}
                </div>
                <div>
                  <p className="font-medium">{tokenTransferDialog.name || 'Geen naam'}</p>
                  <p className="text-sm text-gray-400">{tokenTransferDialog.email}</p>
                </div>
              </div>

              {/* Your Balance */}
              <div className="flex items-center justify-between p-3 bg-[#D4AF37]/10 rounded-lg border border-[#D4AF37]/30">
                <span className="text-gray-300">Jouw saldo:</span>
                <span className="font-bold text-[#D4AF37]">{adminTokenBalance} tokens</span>
              </div>

              {/* Amount Input */}
              <div>
                <Label>Aantal Tokens</Label>
                <Input
                  type="number"
                  min="1"
                  max={adminTokenBalance}
                  value={tokenAmount}
                  onChange={(e) => setTokenAmount(parseInt(e.target.value) || 0)}
                  className="mt-1 bg-white/5 border-white/10"
                />
                <div className="flex gap-2 mt-2">
                  {[5, 10, 25, 50, 100].map((amt) => (
                    <button
                      key={amt}
                      onClick={() => setTokenAmount(amt)}
                      className={`px-3 py-1 rounded text-sm ${
                        tokenAmount === amt 
                          ? 'bg-[#D4AF37] text-black' 
                          : 'bg-white/5 text-gray-300 hover:bg-white/10'
                      }`}
                    >
                      {amt}
                    </button>
                  ))}
                </div>
              </div>

              {/* Reason */}
              <div>
                <Label>Reden (optioneel)</Label>
                <Input
                  value={tokenReason}
                  onChange={(e) => setTokenReason(e.target.value)}
                  placeholder="Bijv: Bonus voor trouwe klant"
                  className="mt-1 bg-white/5 border-white/10"
                />
              </div>

              {/* Warning if not enough tokens */}
              {tokenAmount > adminTokenBalance && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                  Je hebt niet genoeg tokens!
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3">
                <Button
                  onClick={() => setTokenTransferDialog(null)}
                  variant="outline"
                  className="flex-1 border-white/10 text-white hover:bg-white/5"
                >
                  Annuleren
                </Button>
                <Button
                  onClick={async () => {
                    if (tokenAmount <= 0 || tokenAmount > adminTokenBalance) return;
                    setSendingTokens(true);
                    try {
                      const token = localStorage.getItem('token');
                      const res = await axios.post(`${API}/admin/tokens/send`, {
                        member_id: tokenTransferDialog.id,
                        amount: tokenAmount,
                        reason: tokenReason
                      }, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      
                      if (res.data.success) {
                        setAdminTokenBalance(res.data.admin_new_balance);
                        toast.success(`${tokenAmount} tokens verstuurd naar ${tokenTransferDialog.name || tokenTransferDialog.email}!`);
                        setTokenTransferDialog(null);
                        fetchData();
                      }
                    } catch (error) {
                      toast.error(error.response?.data?.detail || 'Kon tokens niet versturen');
                    } finally {
                      setSendingTokens(false);
                    }
                  }}
                  disabled={sendingTokens || tokenAmount <= 0 || tokenAmount > adminTokenBalance}
                  className="flex-1 bg-[#D4AF37] hover:bg-[#B8963A] text-black"
                >
                  {sendingTokens ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Versturen...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Verstuur {tokenAmount} Tokens
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ============== PACKAGE EDIT DIALOG ============== */}
      <Dialog open={packageDialog} onOpenChange={setPackageDialog}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Package className="w-5 h-5 text-[#D4AF37]" />
              {editingPackage ? 'Package Bewerken' : 'Nieuw Package'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Name */}
            <div>
              <Label>Package Naam</Label>
              <Input
                value={packageForm.name}
                onChange={(e) => setPackageForm({ ...packageForm, name: e.target.value })}
                placeholder="Bijv: Premium Membership"
                className="mt-1 bg-white/5 border-white/10"
              />
            </div>

            {/* Description */}
            <div>
              <Label>Beschrijving</Label>
              <Textarea
                value={packageForm.description}
                onChange={(e) => setPackageForm({ ...packageForm, description: e.target.value })}
                placeholder="Beschrijf wat dit package bevat..."
                className="mt-1 bg-white/5 border-white/10"
                rows={3}
              />
            </div>

            {/* Type */}
            <div>
              <Label>Type</Label>
              <Select 
                value={packageForm.type} 
                onValueChange={(val) => setPackageForm({ ...packageForm, type: val })}
              >
                <SelectTrigger className="mt-1 bg-white/5 border-white/10">
                  <SelectValue placeholder="Selecteer type" />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10">
                  <SelectItem value="subscription">Abonnement (maandelijks)</SelectItem>
                  <SelectItem value="lifetime">Lifetime Access (eenmalig)</SelectItem>
                  <SelectItem value="bundle">Bundle (speciale combinatie)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Pricing */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Token Prijs</Label>
                <Input
                  type="number"
                  value={packageForm.token_price}
                  onChange={(e) => setPackageForm({ ...packageForm, token_price: parseInt(e.target.value) || 0 })}
                  placeholder="Bijv: 30"
                  className="mt-1 bg-white/5 border-white/10"
                />
              </div>
              <div>
                <Label>Euro Equivalent (voor weergave)</Label>
                <Input
                  type="number"
                  value={packageForm.euro_price}
                  onChange={(e) => setPackageForm({ ...packageForm, euro_price: parseFloat(e.target.value) || 0 })}
                  placeholder="Bijv: 29.99"
                  className="mt-1 bg-white/5 border-white/10"
                />
              </div>
            </div>

            {/* Image URL */}
            <div>
              <Label>Afbeelding URL</Label>
              <Input
                value={packageForm.image_url}
                onChange={(e) => setPackageForm({ ...packageForm, image_url: e.target.value })}
                placeholder="https://... of selecteer uit gallery"
                className="mt-1 bg-white/5 border-white/10"
              />
              {packageForm.image_url && (
                <div className="mt-2 w-32 h-32 rounded-lg overflow-hidden">
                  <img src={packageForm.image_url} alt="Preview" className="w-full h-full object-cover" />
                </div>
              )}
            </div>

            {/* Gallery Folder Selection */}
            <div>
              <Label>Content Folder (bij aankoop krijgt member toegang tot deze folder)</Label>
              <Select 
                value={packageForm.gallery_folder} 
                onValueChange={(val) => setPackageForm({ ...packageForm, gallery_folder: val })}
              >
                <SelectTrigger className="mt-1 bg-white/5 border-white/10">
                  <SelectValue placeholder="Selecteer folder..." />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10">
                  <SelectItem value="none">Geen folder</SelectItem>
                  {folders.map(folder => (
                    <SelectItem key={folder.id} value={folder.id}>
                      {folder.name} ({folder.count || 0} items)
                    </SelectItem>
                  ))}
                  <SelectItem value="premium">premium</SelectItem>
                  <SelectItem value="vault">vault</SelectItem>
                  <SelectItem value="diamond_content">diamond_content</SelectItem>
                  <SelectItem value="gold_content">gold_content</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                De member krijgt automatisch toegang tot alle content in deze folder na aankoop.
              </p>
            </div>

            {/* Features */}
            <div>
              <Label>Features (één per regel)</Label>
              <Textarea
                value={(packageForm.features || []).join('\n')}
                onChange={(e) => setPackageForm({ 
                  ...packageForm, 
                  features: e.target.value.split('\n').filter(f => f.trim()) 
                })}
                placeholder="HD Kwaliteit&#10;Exclusieve content&#10;Early access"
                className="mt-1 bg-white/5 border-white/10"
                rows={4}
              />
            </div>

            {/* Active Status */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="package-active"
                checked={packageForm.is_active}
                onChange={(e) => setPackageForm({ ...packageForm, is_active: e.target.checked })}
                className="w-4 h-4 rounded border-white/20 bg-white/5"
              />
              <Label htmlFor="package-active">Actief (zichtbaar in de shop)</Label>
            </div>

            {/* Preview Card */}
            <div className="p-4 bg-white/5 rounded-xl border border-white/10">
              <h5 className="text-sm text-gray-400 mb-3">Preview:</h5>
              <div className="flex items-center gap-4">
                {packageForm.image_url ? (
                  <img src={packageForm.image_url} alt="" className="w-16 h-16 rounded-lg object-cover" />
                ) : (
                  <div className="w-16 h-16 rounded-lg bg-white/10 flex items-center justify-center">
                    <Package className="w-8 h-8 text-gray-500" />
                  </div>
                )}
                <div>
                  <h4 className="font-medium">{packageForm.name || 'Package Naam'}</h4>
                  <p className="text-sm text-gray-400">{packageForm.description || 'Beschrijving'}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-[#D4AF37] font-bold">{packageForm.token_price} tokens</span>
                    <span className="text-gray-500">|</span>
                    <span className="text-gray-400">€{packageForm.euro_price}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Save Button */}
            <div className="flex gap-3">
              <Button
                onClick={() => setPackageDialog(false)}
                variant="outline"
                className="flex-1 border-white/10 text-white hover:bg-white/5"
              >
                Annuleren
              </Button>
              <Button
                onClick={async () => {
                  try {
                    const token = localStorage.getItem('token');
                    if (editingPackage) {
                      await axios.put(`${API}/admin/packages/${editingPackage.id}`, packageForm, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      toast.success('Package bijgewerkt!');
                    } else {
                      await axios.post(`${API}/admin/packages`, {
                        ...packageForm,
                        id: packageForm.name.toLowerCase().replace(/\s+/g, '_')
                      }, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      toast.success('Package toegevoegd!');
                    }
                    setPackageDialog(false);
                    fetchData();
                  } catch (error) {
                    toast.error(error.response?.data?.detail || 'Kon package niet opslaan');
                  }
                }}
                className="flex-1 bg-[#D4AF37] hover:bg-[#B8963A] text-black"
              >
                <Save className="w-4 h-4 mr-2" />
                {editingPackage ? 'Bijwerken' : 'Toevoegen'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
